import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  setErrors,
  setReduxPdfApprovals,
  setReduxSignRequest,
} from "redux/appSlice";
import { RequestParticipant, Reviewers } from "models/interfaces";
import { isTruthy } from "helpers/methods";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { fillCopy, isSmsCreditBalanceLow } from "helpers/smsCreditHelpers";
import history from "utils/history";
import { inkfree } from "theme/colors";
import {
  addApprovers,
  addSigner,
  getContactSuggestion,
} from "../../SignRequestHelper";
import { StableSignerDropContainer } from "../../components/SignerDndContainers";
import SignerDragLayer from "../../components/SignerDragLayer";
import {
  InfoCircleGlyph,
  PeopleGlyph,
  PersonGlyph,
} from "../../components/RecipientsIcons";
import MobileSignerCard from "../components/MobileSignerCard";
import MobileApproverCard from "../components/MobileApproverCard";
import MobileApproverGroupCard from "../components/MobileApproverGroupCard";
import MobileDelayDateTimeSheet from "../components/MobileDelayDateTimeSheet";
import MobileDelayDaysSheet from "../components/MobileDelayDaysSheet";
import MobileTextField from "../components/MobileTextField";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";

const emptySigner = {
  id: 0,
  name: "",
  email: "",
  order: 0,
  color: "",
  cc: false,
};

const emptyReviewer = { id: 0, name: "", email: "", order: 0, color: "" };

interface MobileApproversAndSignersProps {
  tabValue: "signer" | "approver";
  setTabValue: (t: "signer" | "approver") => void;
  /** Credits the account has left. */
  smsCreditBalance?: number;
  /** Credits this send will consume — delivery plus SMS verification. */
  smsCreditCost?: number;
  /** The same, with every allowed OTP resend spent. Equals the cost when nobody verifies by text. */
  smsCreditMaxCost?: number;
  /** The balance has loaded and the account can use SMS. */
  showSmsCreditBanner?: boolean;
}

const MobileApproversAndSigners: React.FC<MobileApproversAndSignersProps> = ({
  tabValue,
  setTabValue,
  smsCreditBalance = 0,
  smsCreditCost = 0,
  smsCreditMaxCost = 0,
  showSmsCreditBanner = false,
}) => {
  const theme = useTheme();
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const { signRequest, errors } = useAppSelector(
    (state: RootState) => state.app,
  );

  const [excludeIds, setExcludeIds] = useState<string[]>([]);
  const [contactList, setContactList] = useState<any>({ name: {}, email: {} });
  const [approverExcludeIds, setApproverExcludeIds] = useState<string[]>([]);
  const [approverContactList, setApproverContactList] = useState<any>({
    name: {},
    email: {},
  });
  const [signerToDelay, setSignerToDelay] = useState<
    Partial<RequestParticipant> | Partial<RequestParticipant>[]
  >({ id: -1, order: -1 });
  const [openDateTimeSheet, setOpenDateTimeSheet] = useState(false);
  const [openDaysSheet, setOpenDaysSheet] = useState(false);

  const signerOrderRequired = !!signRequest.signerOrderRequired;
  const reviewers: Reviewers[] = signRequest.approval?.reviewers ?? [];

  // Ensure at least one signer row exists (matches desktop behavior).
  useEffect(() => {
    if (!isTruthy(signRequest.signers) || signRequest.signers?.length === 0) {
      addSigner(emptySigner, signRequest.signers ?? [], dispatch, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setExcludeIds(
      (signRequest.signers ?? [])
        .map((s: RequestParticipant) => s.email)
        .filter(Boolean),
    );
    setContactList({ name: {}, email: {} });
  }, [signRequest.signers]);

  useEffect(() => {
    setApproverExcludeIds(
      reviewers.map((r) => r.email).filter(Boolean) as string[],
    );
    setApproverContactList({ name: {}, email: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signRequest.approval?.reviewers]);

  // Group signers by order (each order level = one drop container / group).
  const signerGroups = useMemo<RequestParticipant[][]>(() => {
    const byOrder: Record<number, RequestParticipant[]> = {};
    (signRequest.signers ?? []).forEach((signer: RequestParticipant) => {
      (byOrder[signer.order] ||= []).push(signer);
    });
    return Object.values(byOrder);
  }, [signRequest.signers]);

  // Build approver rows: individuals + groups (members sharing a groupId).
  const reviewerRows = useMemo(() => {
    const rows: (
      | { type: "individual"; reviewer: Reviewers }
      | { type: "group"; members: Reviewers[] }
    )[] = [];
    const seenGroups = new Set<string | undefined>();
    reviewers.forEach((r) => {
      const isGroup = typeof r.group === "string";
      if (isGroup) {
        if (!seenGroups.has(r.groupId)) {
          seenGroups.add(r.groupId);
          rows.push({
            type: "group",
            members: reviewers.filter((x) => x.groupId === r.groupId),
          });
        }
      } else {
        rows.push({ type: "individual", reviewer: r });
      }
    });
    return rows;
  }, [reviewers]);

  const handleOrderToggle = (checked: boolean) => {
    if (tabValue === "approver") {
      dispatch(setReduxPdfApprovals({ orderNeeded: checked }));
      return;
    }
    dispatch(
      setReduxSignRequest({
        signerOrderRequired: checked,
        signers: signRequest.signers.map(
          (signer: RequestParticipant, index: number) => ({
            ...signer,
            order: index + 1,
            ...(!checked && index === 0
              ? {
                  delayDays: undefined,
                  delayHours: undefined,
                  scheduleTime: undefined,
                }
              : {}),
          }),
        ),
      }),
    );
  };

  const orderChecked =
    tabValue === "approver"
      ? !!signRequest.approval?.orderNeeded
      : signerOrderRequired;

  const onDelayDateTime = (signer: RequestParticipant) => {
    setSignerToDelay(signer);
    setOpenDateTimeSheet(true);
  };
  const onDelayDays = (signer: RequestParticipant) => {
    setSignerToDelay(signer);
    setOpenDaysSheet(true);
  };
  const onAddDelay = (signer: RequestParticipant) => {
    setSignerToDelay(signer);
    setOpenDateTimeSheet(true);
  };

  const handleAddMember = (groupName: string) => {
    addApprovers(emptyReviewer, reviewers, dispatch, true, groupName);
    getContactSuggestion(" ", dispatch, approverExcludeIds);
  };

  const handleAddSigner = () => {
    addSigner(emptySigner, signRequest.signers ?? [], dispatch, false);
    getContactSuggestion(" ", dispatch, excludeIds);
  };

  const handleAddApprover = () => {
    addApprovers(emptyReviewer, reviewers, dispatch);
    getContactSuggestion(" ", dispatch, approverExcludeIds);
  };

  const handleAddApproverGroup = () => {
    addApprovers(emptyReviewer, reviewers, dispatch, true);
    getContactSuggestion(" ", dispatch, approverExcludeIds);
  };

  // Running index across signer groups so each card shows "Signers N".
  let runningIndex = -1;

  const hasInPersonSigner = (signRequest.signers ?? []).some(
    (signer: RequestParticipant) =>
      !signer.cc && signer.deliveryMode === strings.deliveryModes.inPerson,
  );

  const handleHostEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value.substring(0, 256);
    dispatch(
      setReduxSignRequest({
        signRequestConfig: {
          ...signRequest.signRequestConfig,
          inPersonHostEmail: value,
        },
      }),
    );
    dispatch(setErrors({ ...errors, inPersonHostEmail: "" }));
  };

  /**
   * Desktop's SMS-credit callout, in the mobile card idiom. Three reasons to
   * show: this send won't fit (blocking — the server refuses the whole
   * request), resends could empty the wallet mid-signing, or the wallet is low
   * enough that a later level of a sequential request could find it empty.
   * Silent otherwise. Only the first disables Send; see the desktop copy.
   */
  const renderSmsCreditBanner = () => {
    if (tabValue !== "signer" || !showSmsCreditBanner || smsCreditCost === 0) {
      return null;
    }
    const isBlocked = smsCreditBalance < smsCreditCost;
    const maxCost = Math.max(smsCreditMaxCost, smsCreditCost);
    if (!isSmsCreditBalanceLow(smsCreditBalance, smsCreditCost, maxCost))
      return null;

    const headroomNote =
      maxCost > smsCreditCost
        ? ` ${fillCopy(strings.smsCredits.resendHeadroom, {
            resends: strings.SMS_OTP_MAX_RESENDS,
            max: maxCost,
          })}`
        : "";

    const message = isBlocked
      ? `${fillCopy(strings.smsCredits.sendCostShort, {
          cost: smsCreditCost,
          balance: smsCreditBalance,
        })}${headroomNote}`
      : `${fillCopy(strings.smsCredits.sendCost, {
          cost: smsCreditCost,
          balance: smsCreditBalance,
        })} ${fillCopy(strings.smsCredits.lowBalance, {
          balance: smsCreditBalance,
        })}${headroomNote}`;

    return (
      <Box
        role="status"
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "6px",
          p: "10px 12px",
          borderRadius: "10px",
          mb: "10px",
          bgcolor: isBlocked
            ? t.palette.notification.error.bg
            : t.palette.notification.warning.bg,
          border: `1px solid ${
            isBlocked
              ? t.palette.notification.error.border
              : t.palette.notification.warning.border
          }`,
        })}
      >
        <Typography
          sx={(t) => ({
            fontSize: "12.5px",
            fontWeight: 600,
            lineHeight: 1.45,
            color: isBlocked
              ? t.palette.notification.error.text
              : t.palette.notification.warning.text,
          })}
        >
          {message}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => history.push(viewpaths.viewMyPlanViewPath)}
          sx={{
            p: 0,
            border: 0,
            background: "none",
            fontSize: "12.5px",
            fontWeight: 700,
            textDecoration: "underline",
            cursor: "pointer",
            color: inkfree.main,
          }}
        >
          {strings.smsCredits.buyMore}
        </Box>
      </Box>
    );
  };

  const renderHostEmailBanner = () =>
    tabValue === "signer" &&
    hasInPersonSigner && (
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          p: "8px 10px",
          borderRadius: "10px",
          bgcolor: t.palette.mobile.peach[100],
          border: `1px solid ${t.palette.mobile.orange[500]}55`,
          mb: "10px",
        })}
      >
        <Tooltip
          title="The email of whoever will hand their device to each in-person signer."
          arrow
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              width: "fit-content",
            }}
          >
            <PersonGlyph
              sx={{
                width: 14,
                height: 14,
                color: theme.palette.mobile.ePurple[500],
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "12.5px",
                fontWeight: 700,
                color: theme.palette.mobile.base[500],
              }}
            >
              Host email
            </Typography>
            <InfoCircleGlyph
              sx={{
                width: 12,
                height: 12,
                color: theme.palette.mobile.base[200],
              }}
            />
          </Box>
        </Tooltip>
        <MobileTextField
          id="mobile-in-person-host-email"
          placeholder="host@example.com"
          value={signRequest.signRequestConfig?.inPersonHostEmail ?? ""}
          onChange={handleHostEmailChange}
          error={Boolean(errors.inPersonHostEmail)}
          helperText={errors.inPersonHostEmail || undefined}
          inputProps={{ "aria-label": "Host email", maxLength: 256 }}
        />
      </Box>
    );

  return (
    <>
      <SignerDragLayer enabled={tabValue === "signer"} isMobile />

      {/* Order toggle */}
      <Box sx={classes.orderToggleRow}>
        <Typography
          component="label"
          htmlFor="mobile-signer-order-switch"
          sx={classes.orderToggleLabel}
        >
          Set as a order
        </Typography>
        <Switch
          id="mobile-signer-order-switch"
          checked={orderChecked}
          onChange={(e) => handleOrderToggle(e.target.checked)}
          inputProps={{ "aria-label": "Require signing order" }}
        />
      </Box>

      {/* Tabs */}
      <Box sx={classes.tabsRow} role="tablist" aria-label="Recipient type">
        <Box
          sx={classes.tab(tabValue === "signer")}
          {...pressableProps(() => setTabValue("signer"), {
            role: "tab",
            selected: tabValue === "signer",
            selectedAttr: "aria-selected",
          })}
        >
          Signers
        </Box>
        <Box
          sx={classes.tab(tabValue === "approver")}
          {...pressableProps(() => setTabValue("approver"), {
            role: "tab",
            selected: tabValue === "approver",
            selectedAttr: "aria-selected",
          })}
        >
          Approvers
        </Box>
      </Box>

      {tabValue === "signer" ? signerGroups.length === 0 ? (
        <Box sx={classes.recipientsEmptyState}>
          <Box sx={classes.recipientsEmptyStateIcon}>
            <PersonGlyph sx={{ width: 26, height: 26 }} />
          </Box>
          <Typography sx={classes.recipientsEmptyStateTitle}>
            No signers yet
          </Typography>
          <Typography sx={classes.recipientsEmptyStateSubtitle}>
            Add the people who need to sign this document.
          </Typography>
          <Box sx={classes.recipientsEmptyStateActions}>
            <Button variant="contained" disableRipple onClick={handleAddSigner}>
              Add a signer
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          {renderSmsCreditBanner()}
          {renderHostEmailBanner()}
          {signerGroups.map((group) => (
            <StableSignerDropContainer
              key={`group-${group[0].id}`}
              orderKey={group[0].order}
              signers={signRequest.signers}
              dispatch={dispatch}
            >
              {group.map((signer) => {
                runningIndex += 1;
                return (
                  <MobileSignerCard
                    key={signer.id}
                    signer={signer}
                    displayIndex={runningIndex}
                    signerOrderRequired={signerOrderRequired}
                    signers={signRequest.signers}
                    excludeIds={excludeIds}
                    setExcludeIds={setExcludeIds}
                    contactList={contactList}
                    setContactList={setContactList}
                    onDelayDateTime={onDelayDateTime}
                    onDelayDays={onDelayDays}
                    onAddDelay={onAddDelay}
                  />
                );
              })}
            </StableSignerDropContainer>
          ))}
        </Box>
      ) : reviewerRows.length === 0 ? (
        <Box sx={classes.recipientsEmptyState}>
          <Box sx={classes.recipientsEmptyStateIcon}>
            <PeopleGlyph sx={{ width: 26, height: 26 }} />
          </Box>
          <Typography sx={classes.recipientsEmptyStateTitle}>
            No approvers yet
          </Typography>
          <Typography sx={classes.recipientsEmptyStateSubtitle}>
            Without one, the document goes straight to signers.
          </Typography>
          <Box sx={classes.recipientsEmptyStateActions}>
            <Button variant="contained" disableRipple onClick={handleAddApprover}>
              Add approver
            </Button>
            <Button
              variant="contained"
              disableRipple
              onClick={handleAddApproverGroup}
            >
              Add group
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          {reviewerRows.map((row, rowIndex) =>
            row.type === "individual" ? (
              <MobileApproverCard
                key={`rev-${row.reviewer.id}`}
                reviewer={row.reviewer}
                displayIndex={rowIndex}
                allReviewers={reviewers}
                excludeIds={approverExcludeIds}
                setExcludeIds={setApproverExcludeIds}
                contactList={approverContactList}
                setContactList={setApproverContactList}
              />
            ) : (
              <MobileApproverGroupCard
                key={`grp-${row.members[0].groupId}`}
                members={row.members}
                displayIndex={rowIndex}
                allReviewers={reviewers}
                excludeIds={approverExcludeIds}
                setExcludeIds={setApproverExcludeIds}
                contactList={approverContactList}
                setContactList={setApproverContactList}
                onAddMember={handleAddMember}
              />
            ),
          )}
        </Box>
      )}

      <MobileDelayDateTimeSheet
        open={openDateTimeSheet}
        onClose={() => setOpenDateTimeSheet(false)}
        signerToDelay={signerToDelay}
      />

      <MobileDelayDaysSheet
        open={openDaysSheet}
        onClose={() => setOpenDaysSheet(false)}
        signerToDelay={signerToDelay}
      />
    </>
  );
};

/** Adds a new empty signer. Exposed so the shared bottom bar can trigger it. */
export const addMobileSigner = (
  signers: RequestParticipant[],
  dispatch: Function,
) => {
  addSigner(emptySigner, signers ?? [], dispatch, false);
};

/** Adds an individual approver. */
export const addMobileApprover = (
  reviewers: Reviewers[],
  dispatch: Function,
) => {
  addApprovers(emptyReviewer, reviewers ?? [], dispatch);
};

/** Adds a new (empty) approver group. */
export const addMobileGroup = (reviewers: Reviewers[], dispatch: Function) => {
  addApprovers(emptyReviewer, reviewers ?? [], dispatch, true);
};

export default MobileApproversAndSigners;
